import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import Typography from 'components/shared/Typography/Typography';

import { PAYMENT_TYPE_OPTIONS, REPEAT_EVENT_OPTIONS } from '../../../lib/helpers';

import InputWithUnit from './InputWithUnit';

const Installments = ({ isEdit = false, caps }) => {
  const { values } = usePaymentContext();
  const { setFieldValue } = usePaymentContextApi();

  const recurringPayment = values?.recurringPayment;

  const frequencyOptions = caps?.frequency
    ? REPEAT_EVENT_OPTIONS.filter((opt) => opt.value === caps.frequency)
    : REPEAT_EVENT_OPTIONS;

  const currentPaymentType = PAYMENT_TYPE_OPTIONS.find(
    (option) => option.value === recurringPayment?.paymentType
  );

  const currentRepeatEvent = REPEAT_EVENT_OPTIONS.find(
    (option) => option.value === recurringPayment?.repeatEvent
  );

  const maxRepeatEvery = caps?.maxRepeatEvery ?? Infinity;
  const maxAmountPerPayment = caps?.maxAmountPerPayment ?? null;
  const maxPaymentsForCap = (() => {
    if (!caps?.maxDurationMonths) return Infinity;
    const every = Math.max(1, Number(recurringPayment?.repeatEvery) || 1);
    const freq = recurringPayment?.repeatEvent;
    if (freq === 'weekly') return Math.max(1, Math.floor((caps.maxDurationMonths * 4) / every));
    if (freq === 'yearly') return Math.max(1, Math.floor(caps.maxDurationMonths / 12 / every));
    return Math.max(1, Math.floor(caps.maxDurationMonths / every));
  })();

  const handleChange = (key, val) => {
    if (!recurringPayment || !key) return;

    setFieldValue('recurringPayment', {
      ...recurringPayment,
      [key]: val
    });
  };

  const totalAmount = values?.amount ? Number(values.amount) : 0;
  const upfront = Number(recurringPayment?.upfrontPayment) || 0;
  const remainingBalance = Math.max(0, totalAmount - upfront);

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="p" size="small" color="neutral-700">
        Split this invoice total into multiple payments until the balance is $0.
      </Typography>
      <div className="flex flex-col gap-3">
        {!isEdit && (
          <div className="flex flex-col gap-2">
            <div className="flex items-end gap-3">
              <CurrencyInput
                data-qa="upfront-payment"
                label="Upfront payment (Optional)"
                value={recurringPayment?.upfrontPayment}
                onValueChange={(value) => handleChange('upfrontPayment', value || 0)}
                name="upfrontPayment"
                parentClassName="flex-1"
                labelClassName="font-medium text-sm leading-4"
                maxValue={totalAmount || undefined}
              />
              <CurrencyInput
                data-qa="remaining-balance"
                label="Remaining balance"
                value={remainingBalance}
                parentClassName="flex-1"
                labelClassName="font-medium text-sm leading-4"
                disabled
              />
            </div>
            <div className="flex gap-0.5">
              <Icon icon="info-filled" size={14} />
              <Typography variant="p" size="small" color="neutral-700">
                Enter an amount to pay upfront and schedule the rest.
              </Typography>
            </div>
          </div>
        )}
        <div className="flex items-end gap-3">
          <Select
            inputId="payment-type"
            label="Payment type"
            value={currentPaymentType}
            options={PAYMENT_TYPE_OPTIONS}
            onChange={(option) => handleChange('paymentType', option?.value)}
            name="paymentType"
            parentClassName="flex-1"
            isClearable={false}
            menuPortalTarget={document.body}
          />
          {currentPaymentType?.value === 'number-of-payments' ? (
            <Input
              inputId="number-of-payments"
              label="Number of payments"
              data-qa="number-of-payments"
              onChange={(e) => {
                const positive = Math.floor(Math.abs(e.target?.value)) || 1;
                const clamped = Math.min(positive, maxPaymentsForCap);
                handleChange('numberOfPayments', clamped);
              }}
              inputWrapperClassName="w-full flex-1"
              value={recurringPayment?.numberOfPayments}
              name="numberOfPayments"
              type="number"
              max={Number.isFinite(maxPaymentsForCap) ? maxPaymentsForCap : undefined}
            />
          ) : (
            <CurrencyInput
              data-qa="amount-per-payment"
              value={recurringPayment?.amountPerPayment}
              onValueChange={(value) => {
                const next =
                  maxAmountPerPayment != null && Number(value) > maxAmountPerPayment
                    ? maxAmountPerPayment
                    : value;
                handleChange('amountPerPayment', next);
              }}
              name="amountPerPayment"
              parentClassName="w-20 gap-1 flex-1"
              maxValue={
                maxAmountPerPayment != null
                  ? Math.min(remainingBalance, maxAmountPerPayment)
                  : remainingBalance
              }
            />
          )}
        </div>
        <DateInputPicker
          inputId="start-date"
          label="Next installment"
          isDateOnly
          value={recurringPayment?.startDate}
          onChange={(val) => handleChange('startDate', val)}
          name="startDate"
          parentClassName="flex-1"
          isClearable={false}
          minDate={new Date()}
          disabled={isEdit}
        />
        <div className="flex items-center gap-3">
          <Select
            inputId="repeat-event"
            label="Repeat"
            value={currentRepeatEvent}
            options={frequencyOptions}
            onChange={(option) => handleChange('repeatEvent', option?.value)}
            name="repeatEvent"
            icon="repeat-mono"
            parentClassName="flex-1"
            isClearable={false}
            menuPortalTarget={document.body}
            disabled={Boolean(caps?.frequency)}
          />
          <InputWithUnit
            data-qa="repeat-every"
            label="Repeat every"
            onChange={(e) => {
              const positive = Math.floor(Math.abs(e.target?.value)) || 1;
              const clamped = Math.min(positive, maxRepeatEvery);
              handleChange('repeatEvery', clamped);
            }}
            value={recurringPayment?.repeatEvery}
            name="repeatEvery"
            min={1}
            max={Number.isFinite(maxRepeatEvery) ? maxRepeatEvery : undefined}
            unitLabel={`${currentRepeatEvent?.unitLabel}(s)`}
          />
        </div>
      </div>
    </div>
  );
};

export default Installments;
